/*
 * The admin panel: docked on the right so the map stays usable beside it
 * (tools act on hexes clicked there). The world's status, fog for
 * everyone, god view for this connection, operator tools, and a log of
 * what came back.
 */

import { For, Show, createSignal, onCleanup, type JSX } from "solid-js";
import { admin, adminOp, botSeat, forgetBotSeat, godView, log, lookAt, pickHex, status } from "../state/admin";
import { sideName } from "../state/players";

type Tool = "build" | "spawn" | "freight" | "trains" | "hand";
const TOOLS: [Tool, string][] = [
  ["build", "Build"],
  ["spawn", "Spawn"],
  ["freight", "Freight"],
  ["trains", "Trains"],
  ["hand", "Hand over"],
];
const STRUCTURES = ["depot", "station", "barracks", "mine", "plant", "track", "breaker", "guard_post"];
const RECIPES = ["Steelworks", "Munitions plant", "Recruiting office", "Locomotive works", "Copper smelter", "Refinery", "Mint"];
const GOODS = ["Munitions", "Iron ore", "Copper ore", "Coal", "Oil", "Steel", "Copper", "Fuel", "Recruits"];

// The panel's choices, kept while it's closed.
const [tool, setTool] = createSignal<Tool | null>(null);
const [at, setAt] = createSignal<[number, number] | null>(null);
const [to, setTo] = createSignal<[number, number] | null>(null);
const [side, setSide] = createSignal(0);
const [what, setWhat] = createSignal("depot");
const [recipe, setRecipe] = createSignal(RECIPES[0]);
const [design, setDesign] = createSignal(0);
const [count, setCount] = createSignal(5);
const [good, setGood] = createSignal(5);
const [amount, setAmount] = createSignal(1000);
const [handTo, setHandTo] = createSignal(1);
const [picking, setPicking] = createSignal<"at" | "to" | null>(null);

/** A seat for an AI player: a name, then its session token shown once,
 *  with Copy; forgotten on Dismiss (never stored or logged). */
function BotSeat(): JSX.Element {
  const [name, setName] = createSignal("");
  const [copied, setCopied] = createSignal(false);
  return (
    <div class="bot-seat">
      <h4>Seat for an AI player</h4>
      <div class="bot-seat-row">
        <input type="text" placeholder="Its name" value={name()} onInput={(e) => setName(e.currentTarget.value)} onKeyDown={(e) => e.stopPropagation()} />
        <button disabled={!name().trim()} onClick={() => adminOp({ bot_seat: name().trim() })}>
          Make a seat
        </button>
      </div>
      <Show when={botSeat()}>
        {(b) => (
          <div class="bot-token">
            <p>
              Seat for <b>{b().name}</b>: connect with <code>?session=&lt;token&gt;</code> (keep it private).
            </p>
            <div class="bot-seat-row">
              <input type="text" readOnly value={b().token} onFocus={(e) => e.currentTarget.select()} />
              <button
                onClick={() => {
                  void navigator.clipboard?.writeText(b().token).then(() => setCopied(true));
                }}
              >
                {copied() ? "Copied" : "Copy"}
              </button>
              <button
                class="ghost"
                onClick={() => {
                  forgetBotSeat();
                  setCopied(false);
                }}
              >
                Dismiss
              </button>
            </div>
            <p class="muted small">Shown once: it isn't kept anywhere. Dismiss when you've copied it.</p>
          </div>
        )}
      </Show>
    </div>
  );
}

export function AdminPanel(p: { sides: { name: string; color: string }[] }): JSX.Element {
  // The status, every few seconds while open.
  adminOp({ status: true });
  const timer = window.setInterval(() => adminOp({ status: true }), 3000);
  onCleanup(() => {
    clearInterval(timer);
    pickHex(null);
  });

  const pick = (which: "at" | "to") => {
    setPicking(which);
    pickHex((hex) => {
      setPicking(null);
      if (which === "at") setAt(hex);
      else setTo(hex);
      // Track wants its far end next.
      if (which === "at" && tool() === "build" && what() === "track" && !to()) pick("to");
    });
  };
  const choose = (t: Tool) => {
    setTool(t);
    setAt(null);
    setTo(null);
    pick("at");
  };
  const ready = () => !!at() && (tool() !== "build" || what() !== "track" || !!to());
  const run = () => {
    const a = at();
    if (!a) return;
    const t = tool();
    const op: Record<string, unknown> = { side: side() };
    if (t === "build") op.build = { what: what(), at: a, ...(what() === "plant" ? { recipe: recipe() } : {}), ...(what() === "track" ? { to: to() } : {}) };
    else if (t === "spawn") op.spawn = { design: design(), at: a, count: count() };
    else if (t === "freight") op.freight = { at: a, good: good(), amount: amount() };
    else if (t === "trains") op.trains = { at: a, count: count() };
    else if (t === "hand") op.hand = { at: a, to: handTo() };
    adminOp(op);
    // Ready for another of the same: pick its hex.
    setAt(null);
    setTo(null);
    pick("at");
  };
  const sideSelect = (value: () => number, set: (n: number) => void) => (
    <select value={value()} onChange={(e) => set(Number(e.currentTarget.value))}>
      <For each={p.sides}>{(_, i) => <option value={i()}>{sideName(i())}</option>}</For>
    </select>
  );
  const num = (value: () => number, set: (n: number) => void) => (
    <input type="number" value={value()} min="1" onInput={(e) => set(Math.max(1, Number(e.currentTarget.value) || 1))} />
  );
  const hex = (h: [number, number] | null) => (h ? `${h[0]}, ${h[1]}` : "none yet");

  return (
    <aside class="admin-panel" aria-label="Admin">
      <h2>Admin</h2>
      <section>
        <div class="admin-toggles">
          <label>
            <input type="checkbox" checked={admin()?.fog ?? false} onChange={(e) => adminOp({ fog: e.currentTarget.checked })} />
            Fog of war <span class="muted">(everyone)</span>
          </label>
          <label>
            <input type="checkbox" checked={admin()?.god ?? false} onChange={(e) => godView(e.currentTarget.checked)} />
            God view <span class="muted">(just you)</span>
          </label>
        </div>
        <Show when={status()} fallback={<p class="muted">Asking the server…</p>}>
          {(s) => (
            <>
              <p class="muted small">
                Tick {s().tick.toLocaleString("en-US")} · v{s().version} · {s().resident_mb.toLocaleString("en-US")} MB
              </p>
              <table class="admin-sides">
                <thead>
                  <tr>
                    <th>Side</th>
                    <th>Units</th>
                    <th>Seats</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={s().units}>
                    {(n, i) => (
                      <tr>
                        <td>
                          <span class="swatch" style={{ background: p.sides[i()]?.color ?? "#888" }} /> {sideName(i())}
                        </td>
                        <td>{n.toLocaleString("en-US")}</td>
                        <td>{s().seats[i()] ?? 0}</td>
                        <td>
                          <Show when={s().homes[i()]}>{(h) => <button class="ghost" onClick={() => lookAt(h())}>Home</button>}</Show>
                          {/* A side whose commander has left: freed for the
                              next newcomer (only sides someone holds). */}
                          <Show when={s().players.find((x) => x.side === i())}>
                            {(who) => (
                              <button
                                class="ghost"
                                onClick={() => {
                                  if (confirm(`Free side ${i() + 1}? ${who().name} will no longer command it; the next new player takes it.`)) adminOp({ release: i() });
                                }}
                              >
                                Release
                              </button>
                            )}
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              {/* A new side for a player to take: a bare start outside the
                  wall. It can't be taken away again, so it's asked first. */}
              <button
                onClick={() => {
                  if (confirm("Add a new side? A bare start outside the wall, for a player to take. It can't be removed.")) adminOp({ add_side: true });
                }}
              >
                Add a side
              </button>
              <BotSeat />
            </>
          )}
        </Show>
      </section>
      <section>
        <h3>Tools</h3>
        <div class="chips">
          <For each={TOOLS}>
            {([t, label]) => (
              <button class="chip" aria-pressed={tool() === t} onClick={() => choose(t)}>
                {label}
              </button>
            )}
          </For>
        </div>
        <Show when={tool()}>
          <div class="admin-form">
            <span class="row-label">Hex</span>
            <span>
              {hex(at())}{" "}
              <button class="ghost" onClick={() => pick("at")}>
                {picking() === "at" ? "Click the map…" : "Pick"}
              </button>
            </span>
            <span class="row-label">Side</span>
            {sideSelect(side, setSide)}
            <Show when={tool() === "build"}>
              <span class="row-label">What</span>
              <select value={what()} onChange={(e) => setWhat(e.currentTarget.value)}>
                <For each={STRUCTURES}>{(w) => <option value={w}>{w.replace("_", " ")}</option>}</For>
              </select>
              <Show when={what() === "plant"}>
                <span class="row-label">Recipe</span>
                <select value={recipe()} onChange={(e) => setRecipe(e.currentTarget.value)}>
                  <For each={RECIPES}>{(r) => <option>{r}</option>}</For>
                </select>
              </Show>
              <Show when={what() === "track"}>
                <span class="row-label">To</span>
                <span>
                  {hex(to())}{" "}
                  <button class="ghost" onClick={() => pick("to")}>
                    {picking() === "to" ? "Click the map…" : "Pick"}
                  </button>
                </span>
              </Show>
            </Show>
            <Show when={tool() === "spawn"}>
              <span class="row-label">Design</span>
              <select value={design()} onChange={(e) => setDesign(Number(e.currentTarget.value))}>
                <For each={status()?.designs ?? []}>{(d, i) => <option value={i()}>{d}</option>}</For>
              </select>
            </Show>
            <Show when={tool() === "spawn" || tool() === "trains"}>
              <span class="row-label">Count</span>
              {num(count, setCount)}
            </Show>
            <Show when={tool() === "freight"}>
              <span class="row-label">Good</span>
              <select value={good()} onChange={(e) => setGood(Number(e.currentTarget.value))}>
                <For each={GOODS}>{(g, i) => <option value={i()}>{g}</option>}</For>
              </select>
              <span class="row-label">Amount</span>
              {num(amount, setAmount)}
            </Show>
            <Show when={tool() === "hand"}>
              <span class="row-label">To</span>
              {sideSelect(handTo, setHandTo)}
            </Show>
            <span />
            <button class="primary" disabled={!ready()} onClick={run}>
              Do it
            </button>
          </div>
        </Show>
      </section>
      <section>
        <h3>Log</h3>
        <Show when={log().length} fallback={<p class="muted small">Nothing yet.</p>}>
          <ul class="admin-log">
            <For each={log()}>{(l) => <li classList={{ bad: !l.ok }}>{l.text}</li>}</For>
          </ul>
        </Show>
      </section>
    </aside>
  );
}
